
import {defineComponent} from "vue";
import {ImageContentRestDtoModel} from "@/models/api/pharma-cms-content-block/ImageContentRestDtoModel";
import {CodeDescriptionRestDto} from "@/api/pharma-cms-content-block/models";
import PageLinkPicker from "@/components/UI/PageLinkPicker.vue";
import {PageLinkPayload} from "@/models/payload/PageLinkPayload";
import {PageLinkTypeEnum} from "@/models/enum/PageLinkTypeEnum";
import {MultilingualRestDtoModel} from "@/models/MultilingualRestDtoModel";
import {CbStructureMixin} from "@/components/layouts/content-pages/wysiwyg/structures/CbStructureMixin";
import {NormalContentBlockRestService} from "@/services/rest/cms-content-block/NormalContentBlockRestService";
import {FindNormalContentBlockPayload} from "@/models/payload/FindNormalContentBlockPayload";
import {CreateNormalContentBlockPayload} from "@/models/payload/CreateNormalContentBlockPayload";
import {SaveNormalContentBlockPayload} from "@/models/payload/SaveNormalContentBlockPayload";
import {LinkTargetEnum} from "@/models/enum/LinkTargetEnum";
import {EnumClassNameCmsEnum} from "@/models/enum/EnumClassNameCmsEnum";
import {ImageTargetEnum} from "@/api/enums/image-target-enum";
import {CopyCpAbstractPayload} from "@/models/payload/CopyCpAbstractPayload";
import BaseSpinner from "@/components/UI/Bootstrap/BaseSpinner.vue";
import InputSelect from "@/components/UI/InputSelect.vue";
import InputImage from "@/components/UI/InputImage.vue";
import ImageRoundingDetail from "@/components/layouts/content-pages/wysiwyg/misc/ImageRoundingDetail.vue";
import ImageContainerDimensionsDetail
  from "@/components/layouts/content-pages/wysiwyg/misc/ImageContainerDimensionsDetail.vue";
import ImagePositioningDetail from "@/components/layouts/content-pages/wysiwyg/misc/ImagePositioningDetail.vue";
import ExtraMetadataSection from "@/components/layouts/content-pages/wysiwyg/misc/ExtraMetadataSection.vue";
import ImageOverlaysDetail from "@/components/layouts/content-pages/wysiwyg/misc/ImageOverlaysDetail.vue";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";
import {EnumRestService} from "@/services/rest/enum/EnumRestService";

export default defineComponent({
  name: "CbImage",
  extends: CbStructureMixin,
  components: {
    InputImage, InputSelect, ImageRoundingDetail, ExtraMetadataSection, ImageOverlaysDetail,
    ImageContainerDimensionsDetail, ImagePositioningDetail, BaseSpinner, PageLinkPicker
  },
  data: () => ({
    contentBlock: ImageContentRestDtoModel.createWithDefaults(),
    pageLinkPayload: PageLinkPayload.createWithDefaults(),
    docType: 'PAGE_IMG' as string,
    // Bumped whenever the content block is (re)loaded or saved, so the metadata
    // group components remount and re-capture their reset baseline.
    metadataRevision: 0 as number,
    enumBorderTypeCode: [] as CodeDescriptionRestDto[],
    enumBorderWidthCode: [] as CodeDescriptionRestDto[],
    enumHorizontalImageShift: [] as CodeDescriptionRestDto[],
    enumLightboxLayoutCode: [] as CodeDescriptionRestDto[],
    enumVerticalImageShift: [] as CodeDescriptionRestDto[],
  }),
  mounted() {
    this.initialize();
  },
  watch: {
    contentBlock() {
      this.buildPageLinkPayload();

      this.metadataRevision++;

      this.onCopyCpAbstractChanged();
    },
  },
  computed: {
    DpModuleEnum() {
      return DpModuleEnum;
    },
    existingDocumentKey(): string | null {
      return this.contentBlock.image.image_key.nl ?? null;
    },
    metadataSectionId(): string {
      return `cb_image_${this.cbId ?? 'new'}`;
    },
    activeMetadataLabels(): string[] {
      const labels = [] as string[];

      if (this.contentBlock.image.rounding) {
        labels.push(this.$t('cms.rounding'));
      }
      if (this.contentBlock.image.container_dimensions) {
        labels.push(this.$t('cms.container'));
      }
      if (this.contentBlock.image.positioning) {
        labels.push(this.$t('cms.positioning'));
      }
      if (this.contentBlock.image.overlays?.length) {
        labels.push(this.$t('cms.overlays'));
      }

      return labels;
    },
  },
  methods: {
    async find(payload: FindNormalContentBlockPayload): Promise<ImageContentRestDtoModel> {
      return await NormalContentBlockRestService.getInstance().findImage(payload.type, payload.id);
    },
    async create(payload: CreateNormalContentBlockPayload): Promise<string> {
      return await NormalContentBlockRestService.getInstance().createImage(payload.cb as ImageContentRestDtoModel, payload.type);
    },
    async save(payload: SaveNormalContentBlockPayload): Promise<void> {
      await NormalContentBlockRestService.getInstance().saveImage(payload.cb as ImageContentRestDtoModel, payload.type, payload.id);
    },
    async initialize(): Promise<void> {
      [
        this.enumBorderTypeCode,
        this.enumBorderWidthCode,
        this.enumHorizontalImageShift,
        this.enumLightboxLayoutCode,
        this.enumVerticalImageShift
      ] = await Promise.all([
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.BORDER_TYPE, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.BORDER_WIDTH, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.HORIZONTAL_IMAGE_SHIFT, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.LIGHTBOX, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.VERTICAL_IMAGE_SHIFT, DpModuleEnum.CMS)
      ]);
    },
    getDefaultCbModel(): ImageContentRestDtoModel {
      return ImageContentRestDtoModel.createWithDefaults();
    },
    buildPageLinkPayload(): void {
      const externalUrl = this.contentBlock.image.image_url?.nl ?? null;
      const pageId = this.contentBlock.image.image_page_id?.nl ?? null;
      const target = this.contentBlock.image.image_target ?? LinkTargetEnum.SELF;

      this.pageLinkPayload = new PageLinkPayload(externalUrl, pageId, target.toString() as LinkTargetEnum);
    },
    onPageLinkPickerChanged(payload: PageLinkPayload) {
      if (payload.type === PageLinkTypeEnum.NONE) {
        this.contentBlock.image.image_url = undefined;
        this.contentBlock.image.image_page_id = undefined;
        this.contentBlock.image.image_target = undefined;
      } else if (payload.type === PageLinkTypeEnum.URL) {
        this.contentBlock.image.image_url = new MultilingualRestDtoModel(null, null, null, payload.external_url);
        this.contentBlock.image.image_page_id = undefined;
        this.contentBlock.image.image_target = payload.target.toString() as ImageTargetEnum;
      } else if (payload.type === PageLinkTypeEnum.PAGE) {
        this.contentBlock.image.image_url = undefined;
        this.contentBlock.image.image_page_id = new MultilingualRestDtoModel(null, null, null, payload.page_id);
        this.contentBlock.image.image_target = payload.target.toString() as ImageTargetEnum;
      }

      this.onChange();
    },
    onCopyCpAbstractChanged(): void {
      const imageKey = this.contentBlock.image.image_key?.nl ?? null;
      this.copyCpAbstractPayload = new CopyCpAbstractPayload(null, imageKey, null);

      this.$emit('abstractContentChanged', this.copyCpAbstractPayload);
    },
    async setImageKey(key: string | null): Promise<void> {
      this.contentBlock.image.image_key = new MultilingualRestDtoModel(null, null, null, key);

      this.onChange();
    },
    async afterSubmitData(): Promise<void> {
      // What was just saved becomes the new reset baseline of the metadata groups.
      this.metadataRevision++;
    },
    pasteCbMetadata(): void {
      // Reassigning contentBlock (instead of mutating it) is what fires the contentBlock watcher.
      const cbClone = ImageContentRestDtoModel.cloneFrom(this.contentBlock);

      cbClone.cloneMetadataFrom(this.getClipboardContentBlock(this.cbType) as ImageContentRestDtoModel);
      this.contentBlock = cbClone;

      this.buildPageLinkPayload();
    },
  }
});
